import { useState } from 'react';
import {
  Archive,
  ArrowUpRight,
  Check,
  CheckCircle2,
  ExternalLink,
  Forward,
  MessageSquare,
  ShieldCheck,
  UserPlus,
  X,
} from 'lucide-react';

import { Avatar } from './Avatar';
import { Button } from './Button';
import type { InboxItem, InboxItemKind } from '../hooks/use-inbox';

export interface InboxDetailProps {
  item: InboxItem | null;
  onResolve: (id: string, action: string) => void;
  onArchive: (id: string) => void;
  /** Navigate to the owning task in the Tasks tab */
  onOpenTask?: (taskId: string) => void;
  /** Open the full conversation */
  onOpenConversation?: (conversationId: string) => void;
}

type KindCopy = { title: string; primary: string; secondary: string };

const KIND_COPY: Record<InboxItemKind, KindCopy> = {
  approval: { title: 'Approval needed', primary: 'Approve', secondary: 'Reject' },
  review: { title: 'Ready for your review', primary: 'Sign off', secondary: 'Send back' },
  question: { title: 'Agent has a question', primary: 'Answer', secondary: 'Decide later' },
  failure: { title: 'Needs your attention', primary: 'Take action', secondary: 'Skip' },
  suggestion: { title: 'Suggestion', primary: 'Run it', secondary: 'Not now' },
};

function formatTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function EmptyState() {
  return (
    <div
      data-testid="inbox-empty-state"
      className="h-full flex items-center justify-center p-sd-6 text-center"
    >
      <div className="max-w-sm">
        <div className="mx-auto mb-sd-3 w-12 h-12 rounded-full bg-sd-surface-elevated flex items-center justify-center">
          <CheckCircle2 size={22} className="text-sd-success" aria-hidden />
        </div>
        <div className="text-sd-sm font-medium text-sd-text">You’re all caught up</div>
        <div className="text-sd-xs text-sd-text-secondary mt-sd-1">
          When an agent needs approval, finishes a task, or hits a snag, it’ll show up here.
        </div>
      </div>
    </div>
  );
}

// ─── Fake thread data keyed off the item — replace with real task thread on integration
interface ThreadStep {
  label: string;
  at: string;
  status: 'done' | 'current' | 'pending';
  detail?: string;
}

function buildThread(item: InboxItem): ThreadStep[] {
  const received = new Date(item.received_at).getTime();
  return [
    {
      label: 'Task started',
      at: new Date(received - 1000 * 60 * 12).toISOString(),
      status: 'done',
      detail: `Kicked off by ${item.agent.name} from the conversation`,
    },
    {
      label: 'Planning complete',
      at: new Date(received - 1000 * 60 * 8).toISOString(),
      status: 'done',
      detail: 'Drafted 5-step plan with data-access requirements',
    },
    {
      label: 'Tool calls ran',
      at: new Date(received - 1000 * 60 * 3).toISOString(),
      status: 'done',
      detail: '3 tool calls · 2 artifacts produced',
    },
    {
      label:
        item.kind === 'approval'
          ? 'Waiting on your approval'
          : item.kind === 'review'
          ? 'Waiting on your review'
          : item.kind === 'question'
          ? 'Waiting on your answer'
          : item.kind === 'failure'
          ? 'Blocked — needs you'
          : 'Suggestion surfaced',
      at: item.received_at,
      status: 'current',
      detail: item.key_ask,
    },
    {
      label: 'Agent continues',
      at: new Date(received + 1000 * 60 * 5).toISOString(),
      status: 'pending',
    },
  ];
}

export function InboxDetail({
  item,
  onResolve,
  onArchive,
  onOpenTask,
  onOpenConversation,
}: InboxDetailProps) {
  const [reply, setReply] = useState('');

  if (!item) return <EmptyState />;

  const copy = KIND_COPY[item.kind];
  const resolved = !!item.resolved_at;
  const thread = buildThread(item);

  return (
    <div
      data-testid="inbox-detail"
      data-inbox-item-id={item.id}
      className="h-full flex flex-col bg-sd-background"
    >
      {/* Header ─────────────────────────────────────────────────────── */}
      <header className="px-sd-6 pt-sd-5 pb-sd-4 border-b border-sd-border/30">
        <div className="flex items-start gap-sd-3">
          <Avatar initials={item.agent.initials} color={item.agent.color} size="lg" />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-sd-2 text-sd-xs text-sd-text-secondary">
              <ShieldCheck size={12} aria-hidden />
              <span className="font-medium">{copy.title}</span>
              <span aria-hidden>·</span>
              <span>{item.agent.name}</span>
              <span aria-hidden>·</span>
              <span>{formatTime(item.received_at)}</span>
            </div>
            <h2 className="mt-sd-1 text-sd-lg font-semibold text-sd-text text-balance">
              {item.title}
            </h2>
            <p className="mt-sd-1 text-sd-sm text-sd-text-secondary">{item.summary}</p>
          </div>
          <div className="flex items-center gap-sd-1 text-sd-xs">
            {onOpenTask && (
              <button
                type="button"
                onClick={() => onOpenTask(item.task_id)}
                className="inline-flex items-center gap-sd-1 px-sd-2 py-sd-1 rounded hover:bg-sd-surface-elevated text-sd-text-secondary hover:text-sd-text focus-visible:ring-2 focus-visible:ring-sd-primary focus-visible:outline-none"
              >
                Task <ArrowUpRight size={12} aria-hidden />
              </button>
            )}
            {onOpenConversation && (
              <button
                type="button"
                onClick={() => onOpenConversation(item.conversation_id)}
                className="inline-flex items-center gap-sd-1 px-sd-2 py-sd-1 rounded hover:bg-sd-surface-elevated text-sd-text-secondary hover:text-sd-text focus-visible:ring-2 focus-visible:ring-sd-primary focus-visible:outline-none"
              >
                Thread <ExternalLink size={12} aria-hidden />
              </button>
            )}
          </div>
        </div>

        {/* Key ask — the single sentence the agent is asking. Front and center. */}
        {!resolved && (
          <div className="mt-sd-4 rounded-md border border-sd-border/40 bg-sd-surface-elevated/50 px-sd-4 py-sd-3">
            <div className="text-sd-xs uppercase tracking-wide text-sd-text-secondary">
              The ask
            </div>
            <div className="mt-sd-1 text-sd-base text-sd-text">{item.key_ask}</div>
          </div>
        )}

        {resolved && (
          <div
            role="status"
            className="mt-sd-4 inline-flex items-center gap-sd-2 rounded-md bg-sd-success/10 text-sd-success px-sd-3 py-sd-2 text-sd-sm"
          >
            <CheckCircle2 size={14} aria-hidden />
            Resolved · {item.resolved_action}
            {item.resolved_at && (
              <span className="text-sd-text-secondary">· {formatTime(item.resolved_at)}</span>
            )}
          </div>
        )}
      </header>

      {/* Body — scrollable thread context ───────────────────────────── */}
      <div className="flex-1 min-h-0 overflow-auto px-sd-6 py-sd-5">
        <h3 className="text-sd-xs font-medium uppercase tracking-wide text-sd-text-secondary mb-sd-3">
          Task thread
        </h3>
        <ol className="relative border-l border-sd-border/40 ml-sd-2">
          {thread.map((step, i) => (
            <li key={i} className="pl-sd-4 pb-sd-4 relative">
              <span
                aria-hidden
                className={[
                  'absolute -left-[7px] top-sd-1 w-sd-3 h-sd-3 rounded-full border-2',
                  step.status === 'done'
                    ? 'bg-sd-success border-sd-success'
                    : step.status === 'current'
                    ? 'bg-sd-warning border-sd-warning ring-4 ring-sd-warning/20'
                    : 'bg-sd-background border-sd-border',
                ].join(' ')}
              />
              <div className="flex items-baseline justify-between gap-sd-3">
                <div
                  className={[
                    'text-sd-sm',
                    step.status === 'current'
                      ? 'font-semibold text-sd-text'
                      : 'text-sd-text',
                  ].join(' ')}
                >
                  {step.label}
                </div>
                <div className="text-sd-xs text-sd-text-secondary whitespace-nowrap">
                  {formatTime(step.at)}
                </div>
              </div>
              {step.detail && (
                <div className="mt-[2px] text-sd-xs text-sd-text-secondary">{step.detail}</div>
              )}
            </li>
          ))}
        </ol>

        {/* Artifacts placeholder */}
        {item.artifacts_count > 0 && (
          <div className="mt-sd-5">
            <h3 className="text-sd-xs font-medium uppercase tracking-wide text-sd-text-secondary mb-sd-2">
              Artifacts · {item.artifacts_count}
            </h3>
            <div className="grid grid-cols-2 gap-sd-2">
              {Array.from({ length: Math.min(item.artifacts_count, 4) }).map((_, i) => (
                <div
                  key={i}
                  className="rounded border border-sd-border/40 bg-sd-surface-elevated/40 px-sd-3 py-sd-2 text-sd-sm text-sd-text"
                >
                  <div className="text-sd-xs text-sd-text-secondary">File</div>
                  <div className="truncate">artifact-{i + 1}.pdf</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Footer — actions ────────────────────────────────────────────── */}
      {!resolved && (
        <footer className="border-t border-sd-border/30 bg-sd-background px-sd-6 py-sd-4">
          <label htmlFor="inbox-reply" className="sr-only">
            Reply or add context
          </label>
          <div className="flex items-start gap-sd-2">
            <div className="relative flex-1">
              <MessageSquare
                size={14}
                aria-hidden
                className="absolute left-sd-3 top-sd-3 text-sd-text-secondary"
              />
              <textarea
                id="inbox-reply"
                value={reply}
                onChange={(e) => setReply(e.target.value)}
                placeholder="Add context or ask a follow-up… (optional)"
                rows={1}
                className="w-full pl-sd-8 pr-sd-3 py-sd-2 text-sd-sm rounded-md border border-sd-border/40 bg-sd-surface-elevated/30 text-sd-text placeholder:text-sd-text-secondary focus:outline-none focus:ring-2 focus:ring-sd-primary resize-none"
              />
            </div>
          </div>

          <div className="mt-sd-3 flex items-center justify-between gap-sd-2 flex-wrap">
            <div className="flex items-center gap-sd-1">
              <button
                type="button"
                onClick={() => onArchive(item.id)}
                className="inline-flex items-center gap-sd-1 px-sd-2 py-sd-1 rounded text-sd-xs text-sd-text-secondary hover:bg-sd-surface-elevated hover:text-sd-text focus-visible:ring-2 focus-visible:ring-sd-primary focus-visible:outline-none"
              >
                <Archive size={12} aria-hidden /> Archive
              </button>
              <button
                type="button"
                onClick={() => onResolve(item.id, 'escalated')}
                className="inline-flex items-center gap-sd-1 px-sd-2 py-sd-1 rounded text-sd-xs text-sd-text-secondary hover:bg-sd-surface-elevated hover:text-sd-text focus-visible:ring-2 focus-visible:ring-sd-primary focus-visible:outline-none"
              >
                <UserPlus size={12} aria-hidden /> Escalate
              </button>
              <button
                type="button"
                onClick={() => onResolve(item.id, 'reassigned')}
                className="inline-flex items-center gap-sd-1 px-sd-2 py-sd-1 rounded text-sd-xs text-sd-text-secondary hover:bg-sd-surface-elevated hover:text-sd-text focus-visible:ring-2 focus-visible:ring-sd-primary focus-visible:outline-none"
              >
                <Forward size={12} aria-hidden /> Reassign
              </button>
            </div>
            <div className="flex items-center gap-sd-2">
              <Button
                variant="tertiary"
                size="sm"
                onClick={() => onResolve(item.id, 'rejected')}
                leftIcon={<X size={14} aria-hidden />}
              >
                {copy.secondary}
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={() => onResolve(item.id, 'approved')}
                leftIcon={<Check size={14} aria-hidden />}
              >
                {copy.primary}
              </Button>
            </div>
          </div>
        </footer>
      )}
    </div>
  );
}
